// @ts-nocheck
/**
 * Roster status for an event's teams, owned by RecruitLook.
 *
 * RL holds the players; RLE only shows where each roster stands. One request
 * per event covers every team, the answer is kept in memory for the session,
 * and nothing is written to the database. If RL is down the sheet still loads —
 * the Roster column simply reads "—" with a quiet retry.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface RemoteRoster {
  /** null = RecruitLook reported no count. Unknown, NOT an empty roster. */
  player_count: number | null;
  base_roster_count?: number | null;
  submitted_at: string | null;
  locked: boolean;
  roster_url: string | null;
}

export type RosterMap = Record<string, RemoteRoster>;

/** Session cache — memory only, cleared when the tab closes. */
const cache = new Map<string, RosterMap>();

export function useEventRosters(eventId: string | null | undefined) {
  const [rosters, setRosters] = useState<RosterMap | null>(
    eventId ? cache.get(eventId) ?? null : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRosters = useCallback(
    async (force = false) => {
      if (!eventId) return;
      if (!force && cache.has(eventId)) {
        setRosters(cache.get(eventId)!);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data, error: fnErr } = await supabase.functions.invoke("rl-event-rosters", {
          body: { action: "list", event_id: eventId },
        });
        if (fnErr) throw new Error(fnErr.message);
        if (!data?.ok) throw new Error(data?.error || "RecruitLook didn't answer.");
        const map = (data.rosters ?? {}) as RosterMap;
        cache.set(eventId, map);
        setRosters(map);
      } catch (e: any) {
        // An outage on RL's side must never take the Teams tab down.
        setError(e?.message || "Couldn't reach RecruitLook.");
        setRosters(null);
      } finally {
        setLoading(false);
      }
    },
    [eventId]
  );

  useEffect(() => {
    fetchRosters(false);
  }, [fetchRosters]);

  /** Submit on a coach's behalf. RL flips the status; RLE sends no players. */
  const submitFor = useCallback(
    async (teamId: string, reason?: string) => {
      if (!eventId) return { ok: false, error: "No event." };
      const { data, error: fnErr } = await supabase.functions.invoke("rl-event-rosters", {
        body: { action: "submit", event_id: eventId, team_id: teamId, reason: reason || null },
      });
      if (fnErr) return { ok: false, error: fnErr.message };
      if (!data?.ok) return { ok: false, error: data?.error || "RecruitLook refused this." };
      await fetchRosters(true);
      return { ok: true };
    },
    [eventId, fetchRosters]
  );

  return {
    rosters,
    loading,
    error,
    /** True when RL answered — false while loading or after a failure. */
    available: rosters !== null,
    retry: () => fetchRosters(true),
    submitFor,
  };
}
